import * as S from "./styles.ts";
import { useEffect, useState } from "react";
import StandardOptions
  from "@template/blocks/NumberPickerSection/component/NumberPickerTabs/NumberPickerTab/StandardOptions";
import Footer from "@template/blocks/NumberPickerSection/component/NumberPickerTabs/NumberPickerTab/Footer";
import { AnimatePresence } from "framer-motion";
import AdvancedOptions
  from "@template/blocks/NumberPickerSection/component/NumberPickerTabs/NumberPickerTab/AdvancedOptions";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { getNumbers } from "@/redux/slices/numbers/thunks/getNumbers.ts";
import { changeNumberOption } from "@/redux/slices/numbers";
import { useRouter } from "@/lib/helpers/hooks/useRouter.ts";

interface INumberPickerTabProps {
  defaultCountryCode: string;
  defaultAreaCode: string;
  defaultType: string;
  context?: "virtualLandline";
}

const NumberPickerTab = ({
                           defaultCountryCode,
                           defaultAreaCode,
                           defaultType,
                           context,
                         }: INumberPickerTabProps) => {
  const noAutoSearch = context === "virtualLandline";
  const router = useRouter();
  const dispatch = useTypedDispatch();

  const [mounted, setMounted] = useState(false);

  const standardLoading = useTypedSelector(
    (state) => state.numbers.numbers.standard.loading,
  );
  const premiumLoading = useTypedSelector(
    (state) => state.numbers.numbers.premium.loading,
  );
  const loading = standardLoading || premiumLoading;
  const countries = useTypedSelector((state) => state.numbers.countries);
  const options = useTypedSelector((state) => state.numbers.options);

  const showAdvanced = options.showAdvanced;

  const countryCode = options.countryCode;
  const setCountryCode = (val: string) =>
    dispatch(changeNumberOption({ key: "countryCode", value: val }));
  const areaCode = options.areaCode;
  const setAreaCode = (val: string) =>
    dispatch(changeNumberOption({ key: "areaCode", value: val }));
  const numberCategory = options.numberCategory;
  const setNumberType = (val: string) =>
    dispatch(changeNumberOption({ key: "numberType", value: val }));
  const startsWith = options.startsWith;
  const contains = options.contains;
  const endsWith = options.endsWith;

  const foundCountry = countries.find((c) => c.value === countryCode);
  const foundAreaCode = foundCountry?.area_codes.find(
    (a) => a.area_code === areaCode,
  );
  const actualAreaCode = foundAreaCode?.area_code || "";

  const handleSearch = () => {
    if (loading || !mounted) return;
    if (actualAreaCode || !mounted || areaCode) {
      const searchWithPattern =
        (showAdvanced && (contains || endsWith || startsWith)) ||
        foundAreaCode?.area_code_block ||
        false;
      const foundCountry = countries.find((c) =>
        [countryCode].includes(c.value)
      );
      dispatch(
        getNumbers({
          countryCode: foundCountry?.countryCode,
          type: numberCategory || defaultType,
          areaCode: foundAreaCode
            ? foundAreaCode.search_key
            : areaCode || defaultAreaCode,
          pattern: searchWithPattern
            ? `${(foundAreaCode?.area_code_block || "") + startsWith}*${contains}*${endsWith}${areaCode.endsWith(startsWith) ? `&${startsWith}*` : ""}`
            : "",
        })
      );
    }
  };

  useEffect(() => {
    setCountryCode(defaultCountryCode);
    setAreaCode(defaultAreaCode);
    setNumberType(defaultType);
  }, [defaultCountryCode, defaultAreaCode, defaultType]);

  useEffect(() => {
    if ((actualAreaCode || areaCode) && mounted && !noAutoSearch) {
      handleSearch();
    }
  }, [
    actualAreaCode,
    numberCategory,
    startsWith,
    contains,
    endsWith,
    showAdvanced,
    areaCode,
  ]);

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
      handleSearch();
    }
  }, [mounted]);

  useEffect(() => {
    if (mounted) {
      setMounted(false);
    }
  }, [defaultCountryCode, defaultAreaCode, defaultType]);

  const onClickSearch = () => {
    if (context === "virtualLandline") {
      router.push(
        `/phone-number/all-numbers/?areaCode=${areaCode}&countryCode=${countryCode}&startsWith=${showAdvanced ? startsWith : ""}&contains=${showAdvanced ? contains : ""}&endsWith=${showAdvanced ? endsWith : ""}&numberCategory=${showAdvanced ? numberCategory : ""}`,
      );
    }
  };

  return (
    <S.NumberPickerTabWrapper>
      <S.NumberPickerContainer>
        <StandardOptions />
        <AnimatePresence>
          {showAdvanced && <AdvancedOptions mounted={mounted} />}
        </AnimatePresence>
        <Footer onClickSearch={onClickSearch} />
      </S.NumberPickerContainer>
    </S.NumberPickerTabWrapper>
  );
};

export default NumberPickerTab;
